import { z } from "zod"

import { entityIdSchema } from "./identifiers.js"

const key = z.string().regex(/^[a-z0-9][a-z0-9._-]{0,79}$/)
const jurisdiction = z.string().regex(/^[A-Z]{2}-[A-Z0-9_-]{2,64}$/)

export const propertyProviderRegisterV1Schema = z.object({
  registrationId: entityIdSchema,
  providerKey: key,
  displayName: z.string().trim().min(1).max(120),
  adapterKey: key,
  adapterVersion: z.string().trim().min(1).max(64),
  mappingVersion: z.string().trim().min(1).max(64),
  jurisdictionCodes: z.array(jurisdiction).min(1).max(100).refine(
    (values) => new Set(values).size === values.length,
    "Jurisdictions must be unique.",
  ),
  sourceName: z.string().trim().min(1).max(160),
  licenseVersion: z.string().trim().min(1).max(120),
  attribution: z.string().trim().min(1).max(500),
  cachingPermitted: z.boolean(),
  cacheTtlHours: z.number().int().min(1).max(8760).nullable(),
  freshnessMaxAgeDays: z.number().int().min(1).max(3650),
  termsAcknowledged: z.boolean(),
  status: z.enum(["Enabled", "Disabled"]),
}).strict()
  .refine(
    (value) => value.cachingPermitted || value.cacheTtlHours === null,
    {
      path: ["cacheTtlHours"],
      message: "A cache TTL requires caching permission.",
    },
  )
  .refine(
    (value) => value.status === "Disabled" || value.termsAcknowledged,
    {
      path: ["termsAcknowledged"],
      message: "Enabled providers require terms acknowledgment.",
    },
  )

export const propertyCandidatesResolveV1Schema = z.object({
  lookupId: entityIdSchema,
  serviceLocationId: entityIdSchema,
  addressLine1: z.string().trim().min(1).max(160),
  locality: z.string().trim().min(1).max(100),
  region: z.string().length(2).toUpperCase(),
  postalCode: z.string().regex(/^\d{5}(?:-\d{4})?$/),
  countryCode: z.literal("US"),
  jurisdictionCode: jurisdiction,
  maxCandidates: z.number().int().min(1).max(20),
}).strict()

const structure = z.object({
  structureId: z.string().min(1).max(120),
  footprintAreaSqFt: z.number().int().nonnegative().nullable(),
  footprintSemantic: z.enum(["ground_footprint", "unknown"]),
}).strict()

const candidate = z.object({
  parcelId: z.string().min(1).max(160),
  matchedAddress: z.string().min(1).max(300),
  parcelAreaSqFt: z.number().int().nonnegative().nullable(),
  structures: z.array(structure).max(100),
  confidenceMicros: z.number().int().min(0).max(1_000_000),
  retrievedAt: z.iso.datetime({ offset: true }),
}).strict()

const provenance = {
  providerKey: key,
  sourceName: z.string().min(1).max(160),
  adapterVersion: z.string().min(1).max(64),
  mappingVersion: z.string().min(1).max(64),
  licenseVersion: z.string().min(1).max(120),
  attribution: z.string().min(1).max(500),
  freshnessMaxAgeDays: z.number().int().min(1).max(3650),
}

export const propertyLookupResultV1Schema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("available"),
    reason: z.null(),
    ...provenance,
    candidates: z.array(candidate).min(1).max(20),
  }).strict(),
  z.object({
    status: z.literal("unavailable"),
    reason: z.enum([
      "no_provider",
      "provider_disabled",
      "coverage_unavailable",
      "stale_source",
      "rate_limited",
      "provider_unavailable",
      "license_restricted",
      "ambiguous",
      "incompatible",
    ]),
    providerKey: key.nullable(),
    sourceName: z.string().min(1).max(160).nullable(),
    adapterVersion: z.string().min(1).max(64).nullable(),
    mappingVersion: z.string().min(1).max(64).nullable(),
    licenseVersion: z.string().min(1).max(120).nullable(),
    attribution: z.string().min(1).max(500).nullable(),
    freshnessMaxAgeDays: z.number().int().min(1).max(3650).nullable(),
    candidates: z.tuple([]),
  }).strict(),
])
